import React, { useState, useEffect } from 'react';
import { supabase } from '../services/supabase';
import { generateQuotePDF } from '../utils/quoteUtil';

const ManualQuote = ({ part, onSave }) => {
  const [quoteData, setQuoteData] = useState({
    partNumber: part?.partNumber || '',
    partNumberDisplay: part?.partNumber || '',
    partNumberMaster: part?.part_number_master || part?.partNumber || '',
    revision: part?.revision || '',
    description: part?.description || '',
    quantity: '',
    priceEach: '',
    quoteNumber: '',
    date: new Date().toISOString().split('T')[0],
    goodUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
  });

  const [pricingRanges, setPricingRanges] = useState([
    { range: '1-5', price: '10.00' },
    { range: '6-10', price: '9.50' },
    { range: '11-25', price: '9.00' },
    { range: '26-50', price: '8.50' },
    { range: '50+', price: '8.00' },
  ]);

  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [useCustomCustomer, setUseCustomCustomer] = useState(false);
  const [customCustomer, setCustomCustomer] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    contact: '',
    nickname: '',
  });

  useEffect(() => {
    const fetchCustomersAndQuoteNumber = async () => {
      try {
        // Fetch customers
        const { data: customersData, error: customersError } = await supabase
          .from('customers')
          .select('id, name, billing_address, billing_city, billing_state, billing_zip, billing_contact_name, billing_contact_email, nickname');
        if (customersError) throw customersError;
        setCustomers(customersData || []);

        // Fetch the highest quote number from the quotes table
        const { data: quotesData, error: quotesError } = await supabase
          .from('quotes')
          .select('quote_number')
          .order('quote_number', { ascending: false })
          .limit(1);
        if (quotesError) throw quotesError;

        // Extract the numeric part of the quote number and increment
        let nextQuoteNumber = 1000; // Starting base number
        if (quotesData && quotesData.length > 0) {
          const lastQuoteNumber = parseInt(quotesData[0].quote_number, 10);
          if (!isNaN(lastQuoteNumber)) {
            nextQuoteNumber = lastQuoteNumber + 1;
          }
        }

        // Update quoteData with the new quote number
        setQuoteData((prev) => ({ ...prev, quoteNumber: nextQuoteNumber.toString() }));
      } catch (err) {
        console.error('Error fetching data:', err);
        alert('Failed to fetch customers or generate quote number.');
      }
    };

    fetchCustomersAndQuoteNumber();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setQuoteData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCustomCustomerChange = (e) => {
    const { name, value } = e.target;
    setCustomCustomer((prev) => ({ ...prev, [name]: value }));
  };

  const handlePricingRangeChange = (index, field, value) => {
    const updatedRanges = [...pricingRanges];
    updatedRanges[index][field] = value;
    setPricingRanges(updatedRanges);
  };

  const addPricingRange = () => {
    setPricingRanges([...pricingRanges, { range: '', price: '' }]);
  };

  const removePricingRange = (index) => {
    if (pricingRanges.length > 1) {
      setPricingRanges(pricingRanges.filter((_, i) => i !== index));
    }
  };

  const calculatePriceEach = (quantity) => {
    const qty = parseInt(quantity);
    if (isNaN(qty)) return '';

    for (const { range, price } of pricingRanges) {
      if (!range || !price) continue;
      const [min, max] = range.split('-').map((val) => val.trim());
      if (range.endsWith('+')) {
        const minVal = parseInt(min);
        if (!isNaN(minVal) && qty >= minVal) return price;
      } else {
        const minVal = parseInt(min);
        const maxVal = max ? parseInt(max) : Infinity;
        if (!isNaN(minVal) && !isNaN(maxVal) && qty >= minVal && qty <= maxVal) return price;
      }
    }
    return '';
  };

  const handleQuantityChange = (e) => {
    const quantity = e.target.value;
    setQuoteData((prev) => {
      const price = calculatePriceEach(quantity);
      return { ...prev, quantity, priceEach: price };
    });
  };

  const handleSaveAndGeneratePDF = async (e) => {
    e.preventDefault();
    const customerData = useCustomCustomer
      ? customCustomer
      : customers.find((customer) => customer.id === parseInt(selectedCustomer)) || {};

    try {
      // Step 1: Save the quote via the onSave callback
      await onSave({ ...quoteData, pricingRanges, customerData });

      // Step 2: Generate the PDF
      const pdfBlob = await generateQuotePDF({ quoteData: { ...quoteData, pricingRanges }, customerData });
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Quote_${quoteData.quoteNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Error saving or generating PDF:', err);
      alert('Failed to save or generate PDF: ' + err.message);
    }
  };

  return (
    <div className="bg-white rounded-lg p-8 border border-gray-200 shadow-sm max-w-2xl mx-auto">
      <h2 className="text-2xl font-semibold text-gray-900 mb-6">Manual Quote</h2>
      <form onSubmit={handleSaveAndGeneratePDF}>
        <div className="space-y-6">
          {/* Customer Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Customer</label>
            <div className="flex items-center space-x-4 mb-2">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="customerType"
                  checked={!useCustomCustomer}
                  onChange={() => setUseCustomCustomer(false)}
                  className="mr-2"
                />
                Select Existing Customer
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="customerType"
                  checked={useCustomCustomer}
                  onChange={() => setUseCustomCustomer(true)}
                  className="mr-2"
                />
                Enter Custom Customer
              </label>
            </div>
            {!useCustomCustomer ? (
              <select
                value={selectedCustomer}
                onChange={(e) => setSelectedCustomer(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition shadow-sm"
                required
              >
                <option value="">Select Customer</option>
                {customers.map((customer) => (
                  <option key={customer.id} value={customer.id}>
                    {customer.nickname}
                  </option>
                ))}
              </select>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    name="name"
                    value={customCustomer.name}
                    onChange={handleCustomCustomerChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition shadow-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                  <input
                    type="text"
                    name="address"
                    value={customCustomer.address}
                    onChange={handleCustomCustomerChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition shadow-sm"
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                    <input
                      type="text"
                      name="city"
                      value={customCustomer.city}
                      onChange={handleCustomCustomerChange}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition shadow-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                    <input
                      type="text"
                      name="state"
                      value={customCustomer.state}
                      onChange={handleCustomCustomerChange}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition shadow-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Zip</label>
                    <input
                      type="text"
                      name="zip"
                      value={customCustomer.zip}
                      onChange={handleCustomCustomerChange}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition shadow-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contact</label>
                  <input
                    type="text"
                    name="contact"
                    value={customCustomer.contact}
                    onChange={handleCustomCustomerChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition shadow-sm"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Part Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Part Number</label>
              <input
                type="text"
                name="partNumber"
                value={quoteData.partNumber}
                onChange={handleInputChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition shadow-sm"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Part Number Master</label>
              <input
                type="text"
                name="partNumberMaster"
                value={quoteData.partNumberMaster}
                onChange={handleInputChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition shadow-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Revision</label>
              <input
                type="text"
                name="revision"
                value={quoteData.revision}
                onChange={handleInputChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition shadow-sm"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              name="description"
              value={quoteData.description}
              onChange={handleInputChange}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition shadow-sm"
              rows="3"
            />
          </div>

          {/* Quantity and Price */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
              <input
                type="number"
                name="quantity"
                value={quoteData.quantity}
                onChange={handleQuantityChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition shadow-sm"
                min="1"
                
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Price Each ($)</label>
              <input
                type="text"
                name="priceEach"
                value={quoteData.priceEach}
                onChange={handleInputChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition shadow-sm"
                placeholder="Auto-calculated or enter manually"
              />
            </div>
          </div>

          {/* Pricing Ranges */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Pricing Ranges</label>
            {pricingRanges.map((range, index) => (
              <div key={index} className="flex items-center space-x-3 mb-3">
                <input
                  type="text"
                  placeholder="e.g., 1-5 or 50+"
                  value={range.range}
                  onChange={(e) => handlePricingRangeChange(index, 'range', e.target.value)}
                  className="w-1/3 p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition shadow-sm"
                  required
                />
                <input
                  type="text"
                  placeholder="Price ($)"
                  value={range.price}
                  onChange={(e) => handlePricingRangeChange(index, 'price', e.target.value)}
                  className="w-1/3 p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition shadow-sm"
                  required
                />
                <button
                  type="button"
                  onClick={() => removePricingRange(index)}
                  className={`p-2 rounded-lg transition shadow-sm ${
                    pricingRanges.length > 1
                      ? 'bg-red-500 text-white hover:bg-red-600'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                  disabled={pricingRanges.length <= 1}
                >
                  Remove
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={addPricingRange}
              className="mt-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition shadow-sm"
            >
              Add Pricing Range
            </button>
          </div>

          {/* Quote Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Quote Number</label>
              <input
                type="text"
                name="quoteNumber"
                value={quoteData.quoteNumber}
                onChange={handleInputChange}
                className="w-full p-3 border border-gray-300 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition shadow-sm"
                readOnly
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
              <input
                type="date"
                name="date"
                value={quoteData.date}
                onChange={handleInputChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition shadow-sm"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Good Until</label>
              <input
                type="date"
                name="goodUntil"
                value={quoteData.goodUntil}
                onChange={handleInputChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition shadow-sm"
                required
              />
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-8">
          <button
            type="submit"
            className="w-full px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition shadow-sm text-sm font-medium"
          >
            Save & Generate PDF
          </button>
        </div>
      </form>
    </div>
  );
};

export default ManualQuote;